import { CommonModule, formatDate } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { PropertyService } from '../properties/service/property.service';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { Status } from '../../core/shared/interface';
import { CalendarModule } from 'primeng/calendar';
import { TourService } from '../followups/tour.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { usPhoneValidator } from '../../core/shared/validators/us-phone.validator';
import { PhoneUtils } from '../../core/shared/utils/phone.utils';
import { TourChannel, TourStatus } from '../../enums/tour-statuses.enum';
import { SharedModule } from '../../core/shared/shared.module';
import * as moment from 'moment-timezone';
import { TimezoneDateRangeUtil } from '../../core/shared/utils/timezone-date-range.util';
import { finalize } from 'rxjs';
@Component({
  selector: 'app-create-tours',
  standalone: true,
  imports: [
    DialogModule,
    CommonModule,
    ReactiveFormsModule,
    DropdownModule,
    InputTextModule,
    ButtonModule,
    TableModule,
    ToastModule,
    CalendarModule,
    FormsModule,
    NgxMaskDirective,
    SharedModule
  ],
  providers: [MessageService, provideNgxMask()],
  templateUrl: './create-tours.component.html',
  styleUrls: ['./create-tours.component.scss'],
})


export class CreateToursComponent {
  readonly modelUnitLabel = 'Model Unit';
  @Input() tourData: any | null = null;
  @Input() planNames: any | null = null;
  @Input() buttonLabel: any | null = null;
  @Output() closeDialog = new EventEmitter<void>();
  tourForm: FormGroup;
  tourdata: any;
  userInfo: any = null;
  minDate: Date = new Date();
  residentOptions: any[] = [];
  selectedDate: Date | null = null;
  selectedTime: string | null = null;
  availableTimes: { label: string; value: string }[] = [];
  isEditMode = false;
  maxDate!: Date;
  isSubmitting = false;
  isModelUnit = false;
  leadSources: { label: string; value: string }[] = [];
  constructor
    (
      private fb: FormBuilder,
      private propertyService: PropertyService,
      private messageService: MessageService,
      private tourService: TourService,
    ) {
    this.tourForm = this.fb.group({
      customer_name: ['', Validators.required],
      customer_email: ['', [Validators.email]],
      //phone_number: ['+1', [Validators.required, Validators.maxLength(16), Validators.pattern(/^\+1\d{10,15}$/)]], // International format starting with +1
      phone_number: ['', [Validators.required, usPhoneValidator]], // US format xxx-xxx-xxxx
      plan_name: [null, Validators.required],
      date: [null, Validators.required],
      time: [null, Validators.required],
      lockbox_code: [''],
      user_id: [''],
    });
  }

  ngOnInit(): void {
    const isReschedule = this.buttonLabel === 'Reschedule';
    
    if (this.tourData) {
      this.isEditMode = true;
      let phone = this.tourData.phone_number || '';

      if (phone.startsWith('1')) phone = phone.substring(1);
      else if (phone.startsWith('+1')) phone = phone.substring(2);

      // Fetch available times for this date first
      this.goToTimeSelection(this.tourData.date, this.tourData.time);

      this.tourForm.patchValue({
        customer_name: this.tourData.customer_name,
        customer_email: this.tourData.customer_email,
        phone_number: phone,
        plan_name: this.tourData.plan_name,
        date: this.formatDateForInput(this.tourData.date),
        time: this.tourData.time,
        user_id: this.tourData.user_id,
        lockbox_code: this.tourData.lockbox_code,
      });

      if (isReschedule) { // Enable only date & time
        this.isEditMode = false;
        this.tourForm.get('date')?.enable();
        this.tourForm.get('time')?.enable();

        // Disable everything else
        this.tourForm.get('customer_name')?.disable();
        this.tourForm.get('customer_email')?.disable();
        this.tourForm.get('phone_number')?.disable();
        this.tourForm.get('plan_name')?.disable();
        this.tourForm.get('user_id')?.disable();
        this.tourForm.get('lockbox_code')?.disable();
        if (this.tourForm.value.date && this.tourForm.value.time) {
          this.goToTimeSelection(
            this.tourForm.value.date,
            this.tourForm.value.time
          );
        }
      } else { // ✅ Normal Edit View: Your existing logic
        if (this.tourForm.get('date')?.value) this.tourForm.get('date')?.disable();
        if (this.tourForm.get('time')?.value) this.tourForm.get('time')?.disable();
        if (this.tourForm.get('user_id')?.value) {
          this.getAssignedResident();
        }
        this.tourForm.get('user_id')?.disable();
        if (this.tourForm.get('lockbox_code')?.value) this.tourForm.get('lockbox_code')?.disable();
      }
    }

    // Only restrict minDate for New tour
    if (!this.isEditMode) {
      this.minDate = new Date();
    } else {
      // allow past dates so calendar can render value
      this.minDate = undefined as any;
    }

    this.maxDate = new Date();
    this.maxDate.setDate(new Date().getDate() + 29);
    // this.tourForm.get('date')?.valueChanges.subscribe(() => {
    //   this.getAvailableResidents();
    // });

    // this.tourForm.get('time')?.valueChanges.subscribe(() => {
    //   this.getAvailableResidents();
    // });

    this.propertyService.getUserInfo().subscribe(user => {
      this.userInfo = user;
      this.isModelUnit = !!this.userInfo?.property?.modelUnit;
      this.applyModelUnitState();
      //this.tourTimeForEdit(this.tourData);
      const { minDate, maxDate } = TimezoneDateRangeUtil.todayToNextDays(
        this.userInfo.property.timezone,
        29
      );
      this.maxDate = maxDate;
      this.tourService.getLeadSources(this.userInfo.property_id).subscribe({
        next: (sources) => {
          this.leadSources = sources.map(s => ({ label: s, value: s }));
        }
      });
    });
  }

  formatDateForInput(dateString: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    const [month, day, year] = dateString.split('/');
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
  }

  onSubmit() {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    if (this.tourForm.valid) {
      const tourPayload = this.tourForm.value;
      const isReschedule = this.buttonLabel === 'Reschedule';
      if (this.isModelUnit) {
        tourPayload.plan_name = this.modelUnitLabel;
      }
      if(isReschedule){
          tourPayload.rescheduleDate = formatDate(tourPayload.date, 'MM/dd/yyyy', 'en-US');
          // if (tourPayload.time instanceof Date) {
          //   const planDate = formatDate(tourPayload.date, 'yyyy-MM-dd', 'en-US');
          //   tourPayload.planned_date = planDate + ' ' + (tourPayload.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
          //   tourPayload.rescheduleTime = (tourPayload.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
          // }
          tourPayload.rescheduleTime = tourPayload.time;
          const reScheduleDateISO = formatDate(tourPayload.rescheduleDate, 'yyyy-MM-dd', 'en-US');
          tourPayload.planned_date = reScheduleDateISO + ' ' + this.convertTo24Hour(tourPayload.time)
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const timeZoneLong = new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: 'long',
          }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value;
          tourPayload.client_time_zone = timeZone;
          tourPayload.client_formatted_time_zone = timeZoneLong;
          tourPayload.performedBy = this.userInfo.user_id;
          tourPayload.performerName = this.userInfo.fullname;
          tourPayload.performerEmail = this.userInfo.email;
          tourPayload.channel = TourChannel.PORTAL;
          tourPayload.propertyTimezone = this.userInfo.property.timezone;
          this.rescheduleTour(tourPayload);
      }
      else{
        //Add +1 prefix and remove dashes for US number
        tourPayload.phone_number = PhoneUtils.toE164(tourPayload.phone_number);
        if (this.tourData) {
          this.updateTour(tourPayload);
        } else {
          tourPayload.date = formatDate(tourPayload.date, 'MM/dd/yyyy', 'en-US');
          tourPayload.property_id = this.userInfo.property_id;
          tourPayload.property_name = this.userInfo.property.name;
          tourPayload.status = Status.Pending;
          const reScheduleDateISO = formatDate(tourPayload.date, 'yyyy-MM-dd', 'en-US');
          tourPayload.planned_date = reScheduleDateISO + ' ' + this.convertTo24Hour(tourPayload.time)
          //tourPayload.planned_date = `${tourPayload.date} ${tourPayload.time}`
          // if (tourPayload.time instanceof Date) {
          //   tourPayload.planned_date = formatDate(tourPayload.date, 'yyyy-MM-dd', 'en-US');
          //   planDate += ' ' + (tourPayload.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
          //   tourPayload.time = (tourPayload.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
          // }
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const timeZoneLong = new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: 'long',
          }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value;
          tourPayload.client_time_zone = timeZone;
          tourPayload.client_formatted_time_zone = timeZoneLong;
          tourPayload.propertyTimezone = this.userInfo.property.timezone;
          this.createTour(tourPayload);
        }
      }
    }
  }

  preventEnter(event: KeyboardEvent) {
    if (this.isSubmitting) {
      event.preventDefault();
    }
  }

  createTour(tourPayload: any): void {
    this.isSubmitting = true;
    this.tourForm.disable();
    this.tourService.createTour(tourPayload).subscribe(
      (response) => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Tour created successfully!',
        });
        // Close dialog after toast
        setTimeout(() => {
          this.closeDialog.emit();
          // Reset submitting state AFTER dialog closes
          this.isSubmitting = false;
          this.tourForm.enable();
          this.applyModelUnitState();
        }, 1500);
      },
      (error) => {
        const errorMessage = error?.error?.message || 'Failed to create tour!';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
        });

        // Re-enable form so user can retry
        this.isSubmitting = false;
        this.tourForm.enable();
        this.applyModelUnitState();
      }
    )
  }

  updateTour(tourPayload: any): void {
    this.isSubmitting = true;
    this.tourForm.disable();
    if (this.tourData && this.tourData._id) {
      this.tourService.updateTour(this.tourData._id, tourPayload).subscribe(
        (response) => {
          this.isSubmitting = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Tour updated successfully!',
          });
          // Close dialog after toast
          setTimeout(() => {
            this.closeDialog.emit();
            // Reset submitting state AFTER dialog closes
            this.isSubmitting = false;
            this.tourForm.enable();
            this.applyModelUnitState();
          }, 1500);
        },
        (error) => {
          const errorMessage = error?.error?.message || 'Failed to update tour!';
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });

          // Re-enable form so user can retry
          this.isSubmitting = false;
          this.tourForm.enable();
          this.applyModelUnitState();
        }
      );
    }
  }

  cancelAddFollowup(): void {
    this.closeDialog.emit();
  }

  getAssignedResident() {
    const userId = this.tourForm.value.user_id;
    if (!userId) return;

    this.tourService.getAssignedResident(userId).subscribe((res: any) => {
      this.residentOptions = [{
        label: `${res.first_name} ${res.last_name}`,
        value: userId
      }];
    });
  }

  getAvailableResidents() {
    const date = this.tourForm.value.date;
    const time = this.tourForm.value.time;
    console.log('date', date)
    console.log('time', time)
    if (!date || !time) return;
    if (date && time) {
      const tourDate = formatDate(date, 'yyyy-MM-dd', 'en-US');
      const plannedDate = formatDate(`${tourDate} ${time}`, 'yyyy-MM-dd HH:mm', 'en-US');
      this.tourService.getAvailableResidents(plannedDate).subscribe((res: any) => {
        this.residentOptions = res.map((resident: any) => ({
          label: `${resident.first_name} ${resident.last_name}`,
          value: resident.id
        }));
      });
    }
  }

  enforcePrefix(event: any) {
    const phoneCtrl = this.tourForm.get('phone_number');
    const input: HTMLInputElement = event.target; // actual input element

    if (phoneCtrl) {
      let val: string = phoneCtrl.value || '';

      // Always enforce +1 prefix
      if (!val.startsWith('+1')) {
        val = '+1' + val.replace(/^\+?1?/, '');
      }

      // Remove all non-digits after +1
      val = '+1' + val.substring(2).replace(/\D/g, '');

      // Update value
      phoneCtrl.setValue(val, { emitEvent: false });

      // Force cursor to end (fixes numpad typing issue too)
      requestAnimationFrame(() => {
        input.selectionStart = input.selectionEnd = val.length;
      });
    }
  }

  rescheduleTour(tourPayload: any): void {
    this.isSubmitting = true;
    this.tourForm.disable();
    if (this.tourData && this.tourData._id) {
      this.tourService.rescheduleTour(this.tourData._id, tourPayload).subscribe(
        (response) => {
          this.isSubmitting = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Tour is ${TourStatus.RESCHEDULED} successfully`,
          });

          // Close dialog after toast
          setTimeout(() => {
            this.closeDialog.emit();
            // Reset submitting state AFTER dialog closes
            this.isSubmitting = false;
            this.tourForm.enable();
            this.applyModelUnitState();
          }, 1500);
        },
        (error) => {
          const errorMessage = error?.error?.message || `Failed to ${TourStatus.RESCHEDULED} tour!`;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });

          // Re-enable form so user can retry
          this.isSubmitting = false;
          this.tourForm.enable();
          this.applyModelUnitState();
        }
      );
    }
  }

  tourTimeForEdit(tour: any) {
    const tz = this.userInfo?.property?.timezone;

    // combine date + time from backend
    const datetime = `${tour.date} ${tour.time}`; // e.g., "2025-12-03 14:30"

    // convert to property timezone and get JS Date
    const date = moment.tz(datetime, tz);
    const convertedDate = date.toDate();
    // patch into form
    this.tourForm.patchValue({
      time: convertedDate
    });

    // optional: disable field
    this.tourForm.get('time')?.disable({ emitEvent: false });
  }

  goToTimeSelection(date: Date | string, existingTime?: string) {
    this.selectedDate = date instanceof Date ? date : new Date(date);
    if (isNaN(this.selectedDate.getTime())) {
        this.availableTimes = [];
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load available time slots'
        });
        return;
    }
    
    const scheduleDate = formatDate(this.selectedDate, 'MM/dd/yyyy', 'en-US');

    // Convert selected date to weekday
    const weekday = this.selectedDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    
    let propertyId;
    if(this.tourData){
      propertyId = this.tourData.property_id;
    }else{
      propertyId = this.userInfo.property_id;
    }
    this.tourService.getPropertyTimeSlot(propertyId, weekday, scheduleDate).subscribe({
      next: (res: any) => {
        const propertyTimezone = res?.timeZone;
        const utcSlots = res.slots || [];
        this.availableTimes = utcSlots.map((utcTime: string) => {
          const clientTimeStr = this.convertUtcToPropertyTimezone(utcTime, propertyTimezone);
          return {
            label: clientTimeStr,
            value: clientTimeStr
          };
        });
        
        // 🔑 RESCHEDULE
        if (existingTime) {
            console.log('existingTime', existingTime)
            console.log('scheduleDate', scheduleDate)
            console.log('propertyTimezone', propertyTimezone)
            const existingTimePropTz = this.utcToPropertyTimezone(`${scheduleDate} ${existingTime}`, propertyTimezone);
            console.log('existingTimePropTz', existingTimePropTz)
            const exists = this.availableTimes.some(t => t.value === existingTimePropTz);

            if (!exists) {
                this.availableTimes.unshift({
                    label: existingTimePropTz,
                    value: existingTimePropTz
                });
            }

            // Set date and time
            this.tourForm.get('date')?.setValue(this.selectedDate);
            this.tourForm.get('time')?.setValue(existingTimePropTz);
        }

        if (!this.availableTimes.length) {
          let message = 'No time slots available on this date.';
          if(res?.blackoutDate){
            message = 'Tours are not available on this date.';
          }
          this.messageService.add({
            severity: 'warn',
            summary: 'No Slots',
            detail: message
          });
        }
      },
      error: () => {
        this.availableTimes = [];
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load available time slots'
        });
      }
    });
  }

  convertUtcToPropertyTimezone(utcTime: string, targetTimezone: string): string {
    const utcMoment = moment.utc(utcTime, ['HH:mm', moment.ISO_8601]);
    const localMoment = utcMoment.clone();
    return localMoment.format('hh:mm A'); // e.g. 02:00 PM
  }

  utcToPropertyTimezone(utcDateTimeStr: string, targetTimezone: string): string {
      const utcMoment = moment.utc(utcDateTimeStr, 'MM/DD/YYYY hh:mm A', true);
      const localTime = utcMoment.clone().tz(targetTimezone);
      return localTime.format('hh:mm A'); // e.g., 10:30 AM
  }

  convertTo24Hour(time12: string): string {
    if (!time12) return '';
    
    const [time, modifier] = time12.split(' '); // "5:30 PM" => ["5:30", "PM"]
    let [hoursStr, minutes] = time.split(':');
    let hours = parseInt(hoursStr, 10);
    if (modifier.toUpperCase() === 'PM' && hours < 12) {
      hours += 12;
    }
    if (modifier.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }
    const hoursPadded = hours.toString().padStart(2, '0');
    return `${hoursPadded}:${minutes}`;
  }

  private applyModelUnitState(): void {
    if (!this.tourForm) return;

    if (this.isModelUnit) {
      this.planNames = [{ label: this.modelUnitLabel, value: this.modelUnitLabel }];
      this.tourForm.patchValue({ plan_name: this.modelUnitLabel }, { emitEvent: false });
      this.tourForm.get('plan_name')?.disable({ emitEvent: false });
      return;
    }

    if (this.buttonLabel !== 'Reschedule') {
      this.tourForm.get('plan_name')?.enable({ emitEvent: false });
    }
  }
} 
